"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import MedicalHistoryHeader from "../components/MedicalHistoryHeader";
import styles from "../../Consultations/css/patient-overview.module.css";
import Demographics from "../../Consultations/components/Demographics";
import Conditions from "../../Consultations/components/Conditions";
import Medications from "../../Consultations/components/Medications";
import AllergiesOverview from "../../Consultations/components/AllergiesOverview";
import ObstetricsOverview from "../../Consultations/components/ObstetricsOverview";
import { getConsultationHistoryPatient, getPatientConsultationTimeline } from "../../../../api/clinic/consultationApi.js";
import { getProfileByPatient } from "../../../../api/profile/create_profile.js";

const TABS = [
  { id: "demographics", label: "Demographics" },
  { id: "conditions", label: "Conditions" },
  { id: "medications", label: "Medications" },
  { id: "allergies", label: "Allergies" },
  { id: "obstetrics", label: "Obstetrics" },
];

export default function MedicalHistory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { consultationData } = useConsultation();
  const [activeTab, setActiveTab] = useState("demographics");

  const [patient, setPatient] = useState(consultationData?.patient || null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (consultationData?.patient) {
        if (mounted) setPatient(consultationData.patient);
        return;
      }
      if (!id) return;
      try {
        const p = await getConsultationHistoryPatient(id);
        if (mounted) setPatient(p);

        if (!p) {
          const timeline = await getPatientConsultationTimeline(id);
          const p2 = Array.isArray(timeline) && timeline.length ? timeline[0]?.patient : null;
          if (mounted) setPatient(p2 || null);
        }
      } catch {
        if (mounted) setPatient(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, consultationData?.patient]);

  useEffect(() => {
    const patientId = id;
    if (!patientId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setLoadError("");
        const data = await getProfileByPatient(patientId);
        if (cancelled) return;
        setProfileData(data);
      } catch (e) {
        if (cancelled) return;
        setProfileData(null);
        setLoadError(e?.response?.data?.error || e?.message || "Failed to load patient profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const patientFromProfile = profileData?.patient || null;
  const resolvedPatient = {
    ...(patientFromProfile || patient || {}),
    profilenumber:
      profileData?.profile?.profilenumber ??
      patientFromProfile?.profilenumber ??
      patient?.profilenumber,
    medicalaidprovider: profileData?.mainmember?.medicalaidprovider ?? profileData?.mainmember?.medicalAidProvider,
    medicalaidnumber: profileData?.mainmember?.medicalaidnumber ?? profileData?.mainmember?.medicalAidNumber,
  };
  const patientSex = resolvedPatient?.sex || resolvedPatient?.gender;
  const isFemale = String(patientSex ?? "").trim().toLowerCase() === "female";
  const filteredTabs = isFemale ? TABS : TABS.filter((tab) => tab.id !== "obstetrics");

  return (
    <div className={styles.pageWrapper}>
      <Navbar />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          <MedicalHistoryHeader patient={resolvedPatient} />

          {/* Pill Tabs */}
          <div className={styles.tabList}>
            {filteredTabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className={styles.contentBox}>
            <div className={styles.tabContent}>
              {loading && <div className={styles.loader}>Loading...</div>}
              {!loading && loadError && <div className={styles.errorMessage}>{loadError}</div>}

              {!loading && !loadError && activeTab === "demographics" && <Demographics patientData={resolvedPatient} />}
              {!loading && !loadError && activeTab === "conditions" && <Conditions patientData={resolvedPatient} />}
              
              {/* Linked Medications Component */}
              {!loading && !loadError && activeTab === "medications" && <Medications patientData={resolvedPatient} />}
              
              {/* Linked Allergies and Obstetrics */}
              {!loading && !loadError && activeTab === "allergies" && <AllergiesOverview patientData={resolvedPatient} />}
              {!loading && !loadError && activeTab === "obstetrics" && <ObstetricsOverview patientData={resolvedPatient} />}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
